import React, {
  useCallback,
  useRef,
  useState,
  useMemo,
  useEffect,
} from "react";
import {
  Box,
  Button,
  Paper,
  Stack,
  Typography,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItemButton,
  ListItemText,
  CircularProgress,
  IconButton,
} from "@mui/material";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import SmsOutlinedIcon from "@mui/icons-material/SmsOutlined";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import ChatOutlinedIcon from "@mui/icons-material/ChatOutlined";
import PhoneInTalkOutlinedIcon from "@mui/icons-material/PhoneInTalkOutlined";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { jwtDecode } from "jwt-decode";
import ReactFlow, {
  ReactFlowProvider,
  Background,
  Controls,
  MiniMap,
  addEdge,
  Handle,
  MarkerType,
  Position,
  useNodesState,
  useEdgesState,
} from "reactflow";
import "reactflow/dist/style.css";
import {
  useSaveFlowMutation,
  useGetCustomerServicesQuery,
} from "../store/services/flowApi";

const SERVICE_CARD_WIDTH = 220;
const TARGET_WIDTH = 200;

const serviceConfig = {
  sms: {
    label: "Outbound SMS",
    icon: SmsOutlinedIcon,
    bg: "#ffe9f3",
    color: "#ff4fa1",
  },
  rcs: {
    label: "RCS",
    icon: ChatOutlinedIcon,
    bg: "#eef3ff",
    color: "#4a63ff",
  },
  whatsapp: {
    label: "Whatsapp",
    icon: WhatsAppIcon,
    bg: "#e7fbe9",
    color: "#21b35b",
  },
  truecaller: {
    label: "Truecaller",
    icon: PhoneInTalkOutlinedIcon,
    bg: "#e8f1ff",
    color: "#2f8cff",
  },
};

const POST_ACTION_OPTIONS = {
  sms: [
    { key: "delivered", label: "Delivered" },
    { key: "failed", label: "Failed" },
    { key: "notDeliveredUntil", label: "Awaited" },
    { key: "sent", label: "Sent" },
    { key: "linkClicked", label: "Link Clicked" },
    { key: "linkNotClickedUntil", label: "Link Not Clicked Until" },
  ],
  whatsapp: [
    { key: "sent", label: "Sent" },
    { key: "delivered", label: "Delivered" },
    { key: "read", label: "Read" },
    { key: "failed", label: "Failed" },
    { key: "notDeliveredUntil", label: "Awaited" },
    { key: "linkClicked", label: "Link Clicked" },
    { key: "linkNotClickedUntil", label: "Link Not Clicked Until" },
  ],
  truecaller: [
    { key: "sent", label: "Sent" },
    { key: "delivered", label: "Delivered" },
    { key: "read", label: "Read" },
    { key: "failed", label: "Failed" },
    { key: "notDeliveredUntil", label: "Awaited" },
    { key: "linkClicked", label: "Link Clicked" },
    { key: "linkNotClickedUntil", label: "Link Not Clicked Until" },
  ],
  rcs: [
    { key: "delivered", label: "Delivered" },
    { key: "read", label: "Read" },
    { key: "failed", label: "Failed" },
    { key: "notDeliveredUntil", label: "Awaited" },
    { key: "sent", label: "Sent" },
    { key: "linkClicked", label: "Link Clicked" },
    { key: "linkNotClickedUntil", label: "Link Not Clicked Until" },
  ],
};

const actionPrettyLabels = {
  delivered: "Delivered",
  failed: "Failed",
  read: "Read",
  sent: "Sent",
  notDeliveredUntil: "Awaited",
  linkClicked: "Link Clicked",
  linkNotClickedUntil: "Link Not Clicked Until",
};

/* ================= CUSTOM NODES ================= */

const TargetNode = ({ data }) => {
  return (
    <Box
      sx={{
        width: TARGET_WIDTH,
        bgcolor: "#ffffff",
        borderRadius: 999,
        boxShadow: 2,
        border: "1px solid #dde2ee",
        px: 1.4,
        py: 0.6,
        boxSizing: "border-box",
        fontSize: "0.7rem",
        position: "relative",
        cursor: "default",
      }}
    >
      <Handle
        type="source"
        position={Position.Right}
        id="out"
        style={{
          background: "#4c9fff",
          width: 8,
          height: 8,
          borderRadius: "50%",
        }}
      />

      {/* icon + title */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
        }}
      >
        <Box
          sx={{
            width: 18,
            height: 18,
            borderRadius: "50%",
            bgcolor: "#e9f7ff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mr: 0.6,
          }}
        >
          <ChatOutlinedIcon sx={{ fontSize: 11, color: "#27a6ff" }} />
        </Box>

        <Typography sx={{ fontSize: "0.7rem", fontWeight: 600 }}>
          Target Audience
        </Typography>
      </Box>
    </Box>
  );
};

const ServiceNode = ({ data, id }) => {
  const svcMeta = serviceConfig[data.serviceType] || {};
  const IconComp = svcMeta.icon;
  const selectedActions = data.postActions || [];
  const stepNumber = data.order;
  const idLabel =
    data.templateName ||
    data.smsConfig?.templateName ||
    data.whatsappConfig?.templateName ||
    data.rcsConfig?.templateName ||
    data.truecallerConfig?.templateName ||
    data.serviceType?.toUpperCase() ||
    "SVC";
  console.log("ServiceNode data:", idLabel, data);

  const getTop = (index, total) => {
    const start = 40;
    const end = 80;
    if (total <= 1) return `${(start + end) / 2}%`;
    const step = (end - start) / (total - 1);
    return `${start + index * step}%`;
  };

  const total = selectedActions?.length || 0;
  return (
    <Box
      sx={{
        width: SERVICE_CARD_WIDTH,
        bgcolor: "#ffffff",
        borderRadius: 0.4,
        boxShadow: 2,
        border: "1px solid #dde2ee",
        px: 1,
        py: 0.7,
        boxSizing: "border-box",
        fontSize: "0.7rem",
        position: "relative",
      }}
    >
      <Handle
        type="target"
        position={Position.Left}
        id="in"
        style={{
          top: "28%",
          background: "#4c9fff",
          width: 8,
          height: 8,
          borderRadius: "50%",
        }}
      />
      {selectedActions.length > 0 &&
        selectedActions.map((action, index) => {
          const key = action.key || action;

          return (
            <Handle
              key={`R-${key}`}
              type="source"
              position={Position.Right}
              id={`R-${key}`}
              style={{
                top: getTop(index, total),
                background: "#4c9fff",
                width: 8,
                height: 8,
                borderRadius: "50%",
              }}
            />
          );
        })}
      {data.onDeleteNode && (
        <DeleteOutlineIcon
          onClick={(e) => {
            e.stopPropagation();
            data.onDeleteNode();
          }}
          sx={{
            position: "absolute",
            top: 4,
            right: 4,
            fontSize: 16,
            color: "#999",
            cursor: "pointer",
            "&:hover": { color: "red" },
          }}
        />
      )}
      <Typography
        sx={{ fontSize: "0.55rem", color: "text.secondary", mb: 0.1 }}
      >
        Template Name: {idLabel}
      </Typography>

      {/* top row with icon + title */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          mb: 0.6,
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center" }}>
          {IconComp && (
            <Box
              sx={{
                width: 18,
                height: 18,
                borderRadius: "50%",
                bgcolor: svcMeta.bg,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mr: 0.6,
              }}
              onClick={(e) => {
                e.stopPropagation();
                data.onOpenConfig?.(id);
              }}
            >
              <IconComp sx={{ fontSize: 11, color: svcMeta.color }} />
            </Box>
          )}
          <Typography
            sx={{
              fontSize: "0.7rem",
              fontWeight: 600,
            }}
          >
            {data.label}
          </Typography>
        </Box>

        {stepNumber && (
          <Box
            sx={{
              minWidth: 18,
              height: 18,
              borderRadius: "50%",
              bgcolor: "#e5edff",
              color: "#3554d1",
              fontSize: "0.7rem",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              ml: 0.5,
            }}
          >
            {stepNumber}
          </Box>
        )}
      </Box>

      {/* selected post actions list */}
      {selectedActions.length > 0 && (
        <Box sx={{ mt: 0.4 }}>
          {selectedActions?.map((act, i) => {
            const key = act.key || act;
            const minutes = act.minutes;

            return (
              <Box
                key={i}
                sx={{
                  mb: 0.2,
                  display: "flex",
                  alignItems: "center",
                  borderRadius: 0.5,
                  border: "1px solid #d4d7e2",
                  bgcolor: "#f7f9ff",
                  px: 0.4,
                  py: 0.18,
                  fontSize: "0.62rem",
                }}
              >
                • {actionPrettyLabels[key] || key}
                {minutes && (
                  <>
                    {" "}
                    <AccessTimeIcon sx={{ fontSize: 10, ml: 0.3, mr: 0.2 }} />
                    {minutes}m
                  </>
                )}
              </Box>
            );
          })}
        </Box>
      )}

      {selectedActions.length === 0 && (
        <Typography
          sx={{
            fontSize: "0.6rem",
            color: "text.disabled",
            mb: 0.3,
          }}
        >
          No post actions added yet
        </Typography>
      )}

      <Typography
        sx={{
          fontSize: "0.6rem",
          color: "#5a76d4",
          mt: 0.2,
          cursor: "pointer",
        }}
        onClick={() => data.onOpenPostActions?.(id)}
      >
        Add Post Actions
      </Typography>
    </Box>
  );
};

const nodeTypes = {
  targetNode: TargetNode,
  serviceNode: ServiceNode,
};

/* ================= POST ACTIONS PANEL ================= */

const PostActionsPanel = ({ node, draft, setDraft, onClose, onSave }) => {
  if (!node) return null;

  const serviceType = node.data.serviceType;
  const serviceLabel = serviceConfig[serviceType]?.label || "Service";

  const handleToggle = (key) => {
    setDraft((prev) =>
      prev.map((item) => {
        if (item.key !== key) return item;

        const nextChecked = !item.checked;

        // When unchecking Awaited, clear its minutes so a stale value
        // doesn't linger and so the mandatory check resets cleanly.
        if (!nextChecked && key === "notDeliveredUntil") {
          return { ...item, checked: false, minutes: "" };
        }

        return { ...item, checked: nextChecked };
      }),
    );
  };

  const handleMinutesChange = (key, value) => {
    setDraft((prev) =>
      prev.map((item) =>
        item.key === key ? { ...item, minutes: value } : item,
      ),
    );
  };

  // ── Awaited is the ONLY action whose minutes is mandatory when checked.
  //    Others can be left blank.
  const awaitedItem = draft.find((d) => d.key === "notDeliveredUntil");
  const isAwaitedChecked = awaitedItem?.checked;
  const awaitedMinutes = awaitedItem?.minutes;
  const isAwaitedInvalid =
    isAwaitedChecked &&
    (awaitedMinutes === "" ||
      awaitedMinutes === undefined ||
      awaitedMinutes === null ||
      Number.isNaN(Number(awaitedMinutes)) ||
      Number(awaitedMinutes) < 15);

  const isSaveDisabled = isAwaitedInvalid;

  return (
    <Box
      sx={{
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        zIndex: 20,
      }}
    >
      <Paper
        elevation={3}
        sx={{
          width: 300,
          borderRadius: 0.5,
          p: 1.5,
          boxSizing: "border-box",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 0.8,
          }}
        >
          <Typography sx={{ fontSize: "0.85rem", fontWeight: 600 }}>
            {serviceLabel} Post Actions
          </Typography>
          <Typography
            sx={{ fontSize: "1rem", cursor: "pointer" }}
            onClick={onClose}
          >
            ×
          </Typography>
        </Box>

        <Typography
          sx={{ fontSize: "0.65rem", color: "text.secondary", mb: 0.8 }}
        >
          ID: {node.id.toUpperCase()}
        </Typography>

        <Stack spacing={0.4} sx={{ mb: 1.2 }}>
          {draft.map((item) => {
            const isAwaited = item.key === "notDeliveredUntil";

            return (
              <Box
                key={item.key}
                sx={{
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <Checkbox
                  checked={item.checked}
                  onChange={() => handleToggle(item.key)}
                  size="small"
                  sx={{ p: 0.4, mr: 0.5 }}
                />

                <Box
                  sx={{
                    display: "flex",
                    flexGrow: 1,
                    gap: 0.5,
                  }}
                >
                  {/* Label */}
                  <Box
                    sx={{
                      flex: 1,
                      borderRadius: 1,
                      border: "1px solid #e1e4ee",
                      bgcolor: "#fafbff",
                      px: 0.6,
                      py: 0.4,
                      fontSize: "0.7rem",
                    }}
                  >
                    {item.label}
                  </Box>

                  {/* Minutes input — shown for any checked action.
                      Mandatory only for Awaited, optional for the rest. */}
                  {item.checked && (
                    <Box
                      sx={{
                        flex: 1,
                        borderRadius: 1,
                        border:
                          isAwaited && isAwaitedInvalid
                            ? "1px solid #e53935"
                            : "1px solid #e1e4ee",
                        bgcolor: "#fff",
                        px: 0.5,
                        py: 0.3,
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <AccessTimeIcon sx={{ fontSize: 14, mr: 0.3 }} />

                      <input
                        type="number"
                        min={isAwaited ? 15 : 0}
                        placeholder={
                          isAwaited ? "Min (required, ≥15)" : "Min (optional)"
                        }
                        value={item.minutes ?? ""}
                        onChange={(e) =>
                          handleMinutesChange(item.key, e.target.value)
                        }
                        style={{
                          width: "100%",
                          border: "none",
                          outline: "none",
                          fontSize: "0.7rem",
                          background: "transparent",
                        }}
                      />
                    </Box>
                  )}
                </Box>
              </Box>
            );
          })}
        </Stack>

        {/* Helper message when Awaited is invalid */}
        {isAwaitedInvalid && (
          <Typography
            sx={{ fontSize: "0.65rem", color: "#e53935", mb: 1 }}
          >
            Awaited requires minutes of 15 or more.
          </Typography>
        )}

        <Box sx={{ textAlign: "right" }}>
          <Button
            variant="contained"
            size="small"
            disabled={isSaveDisabled}
            sx={{ textTransform: "none", fontSize: "0.75rem" }}
            onClick={onSave}
          >
            Save
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

const WorkflowBuilderInner = () => {
  const recomputeServiceOrder = (nodes, edges) => {
    const target = nodes.find((n) => n.type === "targetNode");
    if (!target) return nodes;

    const edgesFrom = {};
    edges.forEach((e) => {
      if (!edgesFrom[e.source]) edgesFrom[e.source] = [];
      edgesFrom[e.source].push(e);
    });

    const visited = new Set();
    const queue = [];

    visited.add(target.id);
    queue.push(target.id);

    let counter = 1;

    const newNodes = nodes.map((n) => ({
      ...n,
      data: { ...n.data, order: undefined },
    }));

    const findIndex = (id) => newNodes.findIndex((n) => n.id === id);

    while (queue.length) {
      const currentId = queue.shift();
      const outEdges = edgesFrom[currentId] || [];

      outEdges.forEach((edge) => {
        if (visited.has(edge.target)) return;

        visited.add(edge.target);
        queue.push(edge.target);

        const idx = findIndex(edge.target);
        if (idx !== -1 && newNodes[idx].type === "serviceNode") {
          newNodes[idx] = {
            ...newNodes[idx],
            data: {
              ...newNodes[idx].data,
              order: counter,
            },
          };
          counter += 1;
        }
      });
    }

    return newNodes;
  };

  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const workflow = location.state?.workflow;

  const searchParams = new URLSearchParams(location.search);
  const queryName = searchParams.get("name");
  const queryCategory = searchParams.get("category");
  let storedMeta = {};
  try {
    const raw = localStorage.getItem(`workflow-meta-${id}`);
    if (raw) storedMeta = JSON.parse(raw);
    console.log("raw stored meta:", storedMeta?.name);
  } catch (e) {
    console.error("Failed to parse workflow meta from localStorage", e);
  }

  const workflowName = workflow?.name || queryName || storedMeta.name || "";
  const workflowCategory =
    workflow?.category || queryCategory || storedMeta.category || "";

  const reactFlowWrapper = useRef(null);
  const [reactFlowInstance, setReactFlowInstance] = useState(null);
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [saveFlow, { isLoading: isSaving }] = useSaveFlowMutation();
  const [customerId, setCustomerId] = useState("");

  useEffect(() => {
    try {
      const token = localStorage.getItem("accessToken");

      if (!token) {
        console.error("Access token not found");
        return;
      }

      const decoded = jwtDecode(token);

      const id = decoded?.customer_id || decoded?.id || decoded?.userid || "";

      if (!id) {
        console.error("Customer ID not found in token", decoded);
        return;
      }

      setCustomerId(String(id));
    } catch (err) {
      console.error("Invalid access token format", err);
    }
  }, []);

  const {
    data: customerServiceResp,
    isLoading: isServiceLoading,
    isError,
  } = useGetCustomerServicesQuery(customerId, {
    skip: !customerId,
  });

  const services = useMemo(() => {
    if (!customerServiceResp?.data) return [];

    const map = new Map();

    customerServiceResp.data.forEach((item) => {
      if (Number(item.show_service) !== 1) return;

      const key = item.service_name.toLowerCase();

      if (!map.has(key)) {
        map.set(key, {
          serviceType: key,
          serviceName: item.service_name,
          service_id: item.service_id,
        });
      }
    });
    console.log("services in builder:", Array.from(map.values()));
    return Array.from(map.values());
  }, [customerServiceResp]);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(`workflow-graph-${id}`);
      if (stored) {
        const parsed = JSON.parse(stored);
        const savedNodes = parsed.nodes || [];
        const savedEdges = parsed.edges || [];

        if (savedNodes.length || savedEdges.length) {
          setNodes(savedNodes);
          setEdges(savedEdges);
        }
      }
    } catch (err) {
      console.error("Failed to load workflow graph from localStorage", err);
    }
  }, [id, setNodes, setEdges]);

  useEffect(() => {
    try {
      const payload = JSON.stringify({ nodes, edges });
      localStorage.setItem(`workflow-graph-${id}`, payload);
    } catch (err) {
      console.error("Failed to save workflow graph to localStorage", err);
    }
  }, [nodes, edges, id]);

  const [postActionNode, setPostActionNode] = useState(null);
  const [postActionDraft, setPostActionDraft] = useState([]);

  const [defaultPostActions, setDefaultPostActions] = useState([]);

  const closePostActions = () => {
    setPostActionNode(null);
    setPostActionDraft([]);
  };

  const openPostActions = useCallback(
    (nodeId) => {
      const node = nodes.find((n) => n.id === nodeId);
      if (!node || node.type !== "serviceNode") return;

      const serviceType = node.data.serviceType;
      const options = POST_ACTION_OPTIONS[serviceType] || [];
      const currentSelected = node.data.postActions || [];

      const draft = options.map((opt) => {
        const found = currentSelected.find((a) => (a.key || a) === opt.key);

        return {
          ...opt,
          checked: !!found,
          // Preserve any previously-saved minutes value (string or empty)
          minutes:
            found && found.minutes !== undefined && found.minutes !== null
              ? String(found.minutes)
              : "",
        };
      });

      setPostActionNode(node);
      setPostActionDraft(draft);
    },
    [nodes],
  );

  const onEdgeDoubleClick = useCallback(
    (event, edge) => {
      event.stopPropagation();

      setEdges((eds) => {
        const nextEdges = eds.filter((e) => e.id !== edge.id);

        // edge delete hone ke baad order dubara calculate
        setNodes((nds) => recomputeServiceOrder(nds, nextEdges));

        return nextEdges;
      });
    },
    [setEdges, setNodes],
  );

  const nodesWithCallbacks = useMemo(
    () =>
      nodes.map((n) =>
        n.type === "serviceNode"
          ? {
              ...n,
              data: {
                ...n.data,
                onOpenPostActions: openPostActions,

                onDeleteNode: () => {
                  // node delete
                  setNodes((nds) => nds.filter((node) => node.id !== n.id));

                  // connected edges delete
                  setEdges((eds) =>
                    eds.filter((e) => e.source !== n.id && e.target !== n.id),
                  );
                },

                onOpenConfig: () => {
                  const nodeId = n.id;

                  if (n.data.serviceType === "sms") {
                    navigate(`/workflow/${id}/sms?nodeId=${nodeId}`);
                  } else if (n.data.serviceType === "whatsapp") {
                    navigate(`/workflow/${id}/whatsapp?nodeId=${nodeId}`);
                  } else if (n.data.serviceType === "rcs") {
                    navigate(`/workflow/${id}/rcs?nodeId=${nodeId}`);
                  }
                },
              },
            }
          : n,
      ),
    [nodes, openPostActions, navigate, id, setNodes, setEdges],
  );

  const savePostActions = () => {
    if (!postActionNode) return;

    const selectedKeys = postActionDraft
      .filter((d) => d.checked)
      .map((d) => {
        const entry = { key: d.key };

        // Attach minutes only when the user actually entered a positive number.
        // For Awaited, the panel already guarantees a valid ≥15 value before
        // Save is enabled; for others it's entirely optional.
        const rawMinutes = d.minutes;
        if (
          rawMinutes !== "" &&
          rawMinutes !== undefined &&
          rawMinutes !== null
        ) {
          const num = Number(rawMinutes);
          if (!Number.isNaN(num) && num > 0) {
            entry.minutes = num;
          }
        }

        return entry;
      });

    setNodes((nds) =>
      nds.map((n) =>
        n.id === postActionNode.id
          ? {
              ...n,
              data: {
                ...n.data,
                postActions: selectedKeys,
              },
            }
          : n,
      ),
    );

    if (defaultPostActions.length === 0 && selectedKeys.length > 0) {
      setDefaultPostActions(selectedKeys);
    }

    closePostActions();
  };

  const handlePaletteDragStart = (e, payload) => {
    e.dataTransfer.setData("application/block-type", payload.type);
    if (payload.serviceType) {
      e.dataTransfer.setData("application/service-type", payload.serviceType);
    }
    e.dataTransfer.effectAllowed = "move";
  };

  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
  }, []);

  const onDrop = useCallback(
    (event) => {
      event.preventDefault();
      if (!reactFlowWrapper.current || !reactFlowInstance) return;

      const type = event.dataTransfer.getData("application/block-type");
      const serviceType = event.dataTransfer.getData(
        "application/service-type",
      );

      if (!type) return;

      const bounds = reactFlowWrapper.current.getBoundingClientRect();
      const position = reactFlowInstance.project({
        x: event.clientX - bounds.left,
        y: event.clientY - bounds.top,
      });

      if (type === "target") {
        const alreadyTarget = nodes.some((n) => n.type === "targetNode");
        if (alreadyTarget) return;

        const id = `target-${Date.now()}`;
        const newNode = {
          id,
          type: "targetNode",
          position,
          data: {
            nodeId: id,
            label: "Target Audience",
            stats: { sent: 0, delivered: 0, failed: 0 },
            targetFile: null,
          },
        };
        setNodes((nds) => nds.concat(newNode));
        return;
      }

      const config = serviceConfig[serviceType] || {};
      const label = config.label || "Service";

      const id = `svc-${Date.now()}-${Math.random().toString(16).slice(2, 6)}`;

      const initialPostActions =
        defaultPostActions && defaultPostActions.length > 0
          ? defaultPostActions
          : [];

      const newNode = {
        id,
        type: "serviceNode",
        position,
        data: {
          nodeId: id,
          serviceType,
          label,
          stats: { sent: 0, delivered: 0, failed: 0 },
          postActions: initialPostActions,
        },
      };

      setNodes((nds) => nds.concat(newNode));
    },
    [reactFlowInstance, nodes, setNodes, defaultPostActions],
  );

  const onConnect = useCallback(
    (params) => {
      setEdges((eds) => {
        const newEdges = addEdge(
          {
            ...params,
            animated: false,
            type: "smoothstep",
            style: {
              stroke: "#4c9fff",
              strokeWidth: 1.5,
            },
            markerEnd: {
              type: MarkerType.ArrowClosed,
              width: 18,
              height: 18,
            },
          },
          eds,
        );

        setNodes((nds) => recomputeServiceOrder(nds, newEdges));

        return newEdges;
      });
    },
    [setEdges, setNodes],
  );

  const executeWorkflow = async () => {
    if (!workflowName?.trim()) {
      console.log("⚠️ workflowName missing in builder", {
        workflowName,
        locationState: workflow,
        search: location.search,
      });
      alert(
        "Workflow name missing hai (cf_name). Kripya Workflow list se naya workflow create karke wapas try karo.",
      );
      return;
    }

    if (!workflowCategory?.trim()) {
      console.log("⚠️ workflowCategory missing in builder", {
        workflowCategory,
      });
      alert(
        "Workflow category missing hai. Kripya workflow create karte waqt category select karein.",
      );
      return;
    }

    const currentNodes = nodes;
    const currentEdges = edges;

    const target = currentNodes.find((n) => n.type === "targetNode");
    if (!target) {
      alert("Please drag Target Audience first.");
      return;
    }
    if (currentNodes.length === 1) {
      alert("Add at least one channel block.");
      return;
    }

    const BASE_NUMBERS = 1000;
    const incoming = {};
    currentNodes.forEach((n) => {
      incoming[n.id] = 0;
    });
    incoming[target.id] = BASE_NUMBERS;

    const edgesFrom = {};
    currentEdges.forEach((e) => {
      if (!edgesFrom[e.source]) edgesFrom[e.source] = [];
      edgesFrom[e.source].push(e);
    });

    const updatedNodes = currentNodes.map((n) => ({
      ...n,
      data: { ...n.data, stats: { sent: 0, delivered: 0, failed: 0 } },
    }));

    const findIndex = (id) => updatedNodes.findIndex((n) => n.id === id);

    const queue = [target.id];

    while (queue.length) {
      const currentId = queue.shift();
      const idx = findIndex(currentId);
      if (idx === -1) continue;

      const node = updatedNodes[idx];
      const inCount = incoming[currentId] || 0;
      if (inCount === 0) continue;

      let sent = inCount;
      let delivered = 0;
      let failed = 0;

      if (node.type === "targetNode") {
        delivered = 0;
        failed = 0;
      } else {
        delivered = Math.round(sent * 0.8);
        failed = sent - delivered;
      }

      node.data = { ...node.data, stats: { sent, delivered, failed } };

      const outEdges = edgesFrom[currentId] || [];
      outEdges.forEach((edge) => {
        const rawHandle = edge.sourceHandle || "sent";
        const key = rawHandle.replace(/^R-/, "").replace(/^L-/, "");

        let metricKey = "sent";
        if (key === "delivered") metricKey = "delivered";
        else if (key === "failed") metricKey = "failed";

        const nodeStats = node.data.stats || {};
        const amount = nodeStats[metricKey] ?? nodeStats.sent ?? 0;

        if (!incoming[edge.target]) incoming[edge.target] = 0;
        incoming[edge.target] += amount;

        if (!queue.includes(edge.target)) queue.push(edge.target);
      });
    }

    setNodes(updatedNodes);
    const serviceNodes = updatedNodes.filter((n) => n.type === "serviceNode");

    const serviceNodesSorted = [...serviceNodes].sort((a, b) => {
      const oa = a.data.order ?? 99999;
      const ob = b.data.order ?? 99999;
      if (oa === ob) return 0;
      return oa - ob;
    });

    const stepMap = {};
    serviceNodesSorted.forEach((node, idx) => {
      const stepNumber = idx + 1;
      stepMap[node.id] = stepNumber;
    });

    const servicesPayload = serviceNodesSorted.map((n) => {
      let configData = {};

      if (n.data.serviceType === "sms") {
        configData = n.data.smsConfig || {};
      } else if (n.data.serviceType === "whatsapp") {
        configData = n.data.whatsappConfig || {};
      } else if (n.data.serviceType === "rcs") {
        configData = n.data.rcsConfig || {};
      } else if (n.data.serviceType === "truecaller") {
        configData = n.data.truecallerConfig || {};
      }

      const outEdges = edgesFrom[n.id] || [];

      const connections = outEdges
        .filter((e) => stepMap[e.target] != null)
        .map((e) => {
          const rawHandle = e.sourceHandle || "sent";
          const key = rawHandle.replace(/^R-/, "").replace(/^L-/, "");
          return {
            to: stepMap[e.target],
            via: key,
          };
        });

      return {
        orderid: stepMap[n.id],
        serviceType: n.data.serviceType,
        postActions: n.data.postActions || [],
        connections,
        data: configData,
      };
    });

    localStorage.setItem(
      `workflow-services-${id}`,
      JSON.stringify(servicesPayload),
    );

    const saved = localStorage.getItem("workflows");
    let list = saved ? JSON.parse(saved) : [];

    list = list.map((w) =>
      w.id === Number(id)
        ? {
            ...w,
            status: "Executed",
            lastRunOn: new Date().toISOString(),
          }
        : w,
    );

    localStorage.setItem("workflows", JSON.stringify(list));

    try {
      const payloadForApi = {
        cf_name: workflowName,
        category: workflowCategory,
        flows: servicesPayload,
      };

      console.log("Sending payload to /flow/save:", payloadForApi);

      const resp = await saveFlow(payloadForApi).unwrap();
      console.log("Flow saved on server:", resp);

      alert("Workflow executed & saved to server successfully!");
      navigate("/workflow");
    } catch (err) {
      console.error("Failed to save flow on server:", err);
      alert(
        "Workflow executed locally, but failed to save on server. Please try again.",
      );
    }
  };

  const isEmpty = nodes.length === 0;

  return (
    <Box
      sx={{
        display: "flex",
        height: "calc(100vh - 64px)",
        bgcolor: "#eef1f7",
      }}
    >
      {/* MAIN AREA */}
      <Box sx={{ flexGrow: 1, display: "flex", flexDirection: "column" }}>
        {/* TOP HEADER */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 3,
            py: 1.25,
          }}
        >
          <Paper
            elevation={0}
            sx={{
              px: 1.75,
              py: 0.5,
              borderRadius: 999,
              bgcolor: "#ffffff",
              minWidth: 180,
            }}
          >
            <Typography sx={{ fontSize: "0.85rem", fontWeight: 500 }}>
              {workflowName}
            </Typography>
          </Paper>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <IconButton
              onClick={() => window.history.back()}
              size="small"
              sx={{ mr: 1 }}
            >
              <ArrowBackIcon fontSize="small" sx={{ color: "#6b31d8" }} />
            </IconButton>
            <Button
              size="small"
              variant="contained"
              sx={{ textTransform: "none", fontSize: "0.78rem" }}
              onClick={executeWorkflow}
              disabled={isSaving}
            >
              {isSaving ? "Saving..." : "Execute"}
            </Button>
          </Box>
        </Box>

        <Box sx={{ flexGrow: 1, display: "flex", gap: 1.5, px: 1.5, pb: 1.5 }}>
          <Paper
            elevation={0}
            sx={{
              flexGrow: 1,
              position: "relative",
              bgcolor: "#f5f7fb",
              borderRadius: 2,
              border: "1px solid #dde2ee",
              overflow: "hidden",
            }}
          >
            <Box
              ref={reactFlowWrapper}
              sx={{
                width: "100%",
                height: "100%",
              }}
              onDrop={onDrop}
              onDragOver={onDragOver}
            >
              <ReactFlow
                nodes={nodesWithCallbacks}
                edges={edges}
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesChange}
                onConnect={onConnect}
                onInit={setReactFlowInstance}
                nodeTypes={nodeTypes}
                fitView
                proOptions={{ hideAttribution: true }}
                onEdgeDoubleClick={onEdgeDoubleClick}
              >
                <Background />
                <MiniMap />
                <Controls />
              </ReactFlow>

              {isEmpty && (
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    textAlign: "center",
                    color: "text.secondary",
                    pointerEvents: "none",
                  }}
                >
                  <Typography sx={{ fontSize: "0.8rem", mb: 0.6 }}>
                    Drag <strong>Target Audience</strong> from right panel.
                  </Typography>
                  <Typography sx={{ fontSize: "0.72rem" }}>
                    Then drag Outbound SMS / WhatsApp / RCS / Truecaller cards
                    to build the journey.
                  </Typography>
                </Box>
              )}

              <PostActionsPanel
                node={postActionNode}
                draft={postActionDraft}
                setDraft={setPostActionDraft}
                onClose={closePostActions}
                onSave={savePostActions}
              />
            </Box>
          </Paper>

          <Paper
            elevation={1}
            sx={{
              width: 230,
              borderRadius: 2,
              bgcolor: "#ffffff",
              p: 1.25,
              boxSizing: "border-box",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, mb: 0.6 }}>
              Journey Builder
            </Typography>

            <Typography
              sx={{
                fontSize: "0.7rem",
                fontWeight: 500,
                mb: 0.3,
                color: "text.secondary",
              }}
            >
              Starting Points
            </Typography>

            <Box
              draggable
              onDragStart={(e) => handlePaletteDragStart(e, { type: "target" })}
              sx={{
                display: "flex",
                alignItems: "center",
                mb: 1.2,
                cursor: "grab",
              }}
            >
              <Box
                sx={{
                  width: 22,
                  height: 22,
                  borderRadius: "50%",
                  bgcolor: "#e9f7ff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mr: 1,
                }}
              >
                <ChatOutlinedIcon sx={{ fontSize: 14, color: "#27a6ff" }} />
              </Box>
              <Typography sx={{ fontSize: "0.75rem", color: "#8f9bb3" }}>
                Target Audience
              </Typography>
            </Box>

            <Typography
              sx={{
                fontSize: "0.7rem",
                fontWeight: 500,
                mb: 0.3,
                color: "text.secondary",
              }}
            >
              Outbound Channels
            </Typography>

            <Stack spacing={1.2} sx={{ mb: 1.2 }}>
              {services.map((svc) => {
                const cfg = serviceConfig[svc.serviceType];
                if (!cfg) return null;

                const Icon = cfg.icon;

                return (
                  <Box
                    key={svc.serviceType}
                    draggable
                    onDragStart={(e) =>
                      handlePaletteDragStart(e, {
                        type: "service",
                        serviceType: svc.serviceType,
                      })
                    }
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      cursor: "grab",
                    }}
                  >
                    <Box
                      sx={{
                        width: 22,
                        height: 22,
                        borderRadius: "50%",
                        bgcolor: cfg.bg,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mr: 0.75,
                      }}
                    >
                      <Icon sx={{ fontSize: 14, color: cfg.color }} />
                    </Box>

                    <Typography sx={{ fontSize: "0.72rem" }}>
                      {cfg.label}
                    </Typography>
                  </Box>
                );
              })}
            </Stack>

            {/* FLOW CONTROLS */}
            <Typography
              sx={{
                fontSize: "0.7rem",
                fontWeight: 500,
                mb: 0.3,
                color: "text.secondary",
              }}
            >
              Flow Controls
            </Typography>

            <Stack direction="row" spacing={1.5}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  cursor: "default",
                }}
              >
                <Box
                  sx={{
                    width: 22,
                    height: 22,
                    borderRadius: "50%",
                    bgcolor: "#fff4e3",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mr: 0.75,
                  }}
                >
                  <AccessTimeIcon sx={{ fontSize: 14, color: "#ff9800" }} />
                </Box>
                <Typography sx={{ fontSize: "0.72rem" }}>Wait</Typography>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  cursor: "default",
                }}
              >
                <Box
                  sx={{
                    width: 22,
                    height: 22,
                    borderRadius: "50%",
                    bgcolor: "#ffe9f4",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mr: 0.75,
                  }}
                >
                  <CompareArrowsIcon sx={{ fontSize: 14, color: "#e0419c" }} />
                </Box>
                <Typography sx={{ fontSize: "0.72rem" }}>
                  Switch Identifier
                </Typography>
              </Box>
            </Stack>
          </Paper>
        </Box>
      </Box>
    </Box>
  );
};

const WorkflowBuilder = () => (
  <ReactFlowProvider>
    <WorkflowBuilderInner />
  </ReactFlowProvider>
);

export default WorkflowBuilder;